import { useQuery } from "@tanstack/react-query";
import type { SelectableMetric } from "@/components/entity-column-selector";
import { extractCampaignMetrics } from "@/lib/campaign-metrics";
import { extractSalespersonMetrics } from "@/lib/salesperson-metrics";
import { extractWebpageMetrics } from "@/lib/webpage-metrics";
import type { EntityType } from "@/types/entity";
import type { MetricDefinition } from "./use-field-definitions";

/**
 * Gets metric definitions for the requested entity.
 * Currently returns locally configured metrics, but structured to easily
 * switch to remote API fetching later.
 *
 * Response is cached for 10 min because this metadata rarely changes.
 */
export function useMetricsDefinitions(entity: EntityType) {
  return useQuery<MetricDefinition[], Error>({
    queryKey: ["metrics-definitions", entity],
    queryFn: async () => {
      // For now, return local metrics based on entity type
      // Later this can be changed to: api.get<MetricDefinition[]>(`datahub/${entity}/metrics-definitions/`)

      switch (entity) {
        case "campaigns": {
          const { formulaMetrics } = extractCampaignMetrics();
          return formulaMetrics;
        }
        case "webpages": {
          const { formulaMetrics } = extractWebpageMetrics();
          return formulaMetrics;
        }
        case "salespeople": {
          const { formulaMetrics } = extractSalespersonMetrics();
          return formulaMetrics;
        }
        case "accounts":
        case "opportunities":
          // TODO: Add metrics extraction for these entities when needed
          return [];
        default:
          return [];
      }
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Gets metrics in SelectableMetric format for UI components.
 * This provides metrics formatted for use in column selectors and other UI components.
 *
 * Response is cached for 10 min because this metadata rarely changes.
 */
export function useSelectableMetrics(entity: EntityType) {
  return useQuery<SelectableMetric[], Error>({
    queryKey: ["selectable-metrics", entity],
    queryFn: async () => {
      // For now, return local metrics based on entity type
      // Later this can be changed to: api.get<SelectableMetric[]>(`datahub/${entity}/selectable-metrics/`)

      switch (entity) {
        case "campaigns": {
          const { selectableMetrics } = extractCampaignMetrics();
          return selectableMetrics;
        }
        case "webpages": {
          const { selectableMetrics } = extractWebpageMetrics();
          return selectableMetrics;
        }
        case "salespeople": {
          const { selectableMetrics } = extractSalespersonMetrics();
          return selectableMetrics;
        }
        case "accounts":
        case "opportunities":
          // TODO: Add metrics extraction for these entities when needed
          return [];
        default:
          return [];
      }
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}
